import { OperationVariables, TypedDocumentNode } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import { Box, Button, Stack, Typography } from "@mui/material";
import { GridRowParams, GridSortModel, GridValidRowModel } from "@mui/x-data-grid-premium";
import { ExportDataButton } from "components/ExportDataButton";
import { WASTE_CONTAINER_TYPES } from "encamp-shared/src/constants/wasteContainerTypes";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  ClockStatus,
  FacilityWasteContainersQuery,
  FacilityWasteContainersQueryVariables,
  TenantWasteContainersQuery,
  TenantWasteContainersQueryVariables,
  WasteClassification,
  WasteContainerListItemFragment,
  WasteContainerStatus,
} from "generated-graphql/graphql";
import {
  GridSelectionProvider,
  OmnisearchCommandButtonsContext,
  OmnisearchDataGridV2,
} from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { prettyPrintWasteClassification } from "../Facilities/Facility/prettyPrintWasteClassification";
import { WasteListPageSurface } from "../WasteListPageSurface";
import { FACILITY_WASTE_CONTAINERS_QUERY, TENANT_WASTE_CONTAINERS_QUERY } from "./api";
import {
  accumulationAreaLabel,
  CLOCK_STATUS_LABELS,
  containerTypeLabel,
  CONTAINER_STATUS_LABELS,
  NO_VALUE_PLACEHOLDER,
  formatAmountCapacity,
} from "./containerDisplay";
import { ClockCell, FillCell, WasteStreamCell } from "./ContainerListCells";
import { ContainersFilterBar } from "./ContainersFilterBar";
import { ContainerStatTiles } from "./ContainerStatTiles";
import { BulkExportLabelsButton } from "./Label/BulkExportLabelsDialog";
import { WasteContainerDialog } from "./WasteContainerDialog";
import { WasteContainerDrawer } from "./WasteContainerDrawer";
import { WasteContainerStatusChip } from "./WasteContainerStatusChip";

type TenantContainerRow = NonNullable<
  NonNullable<TenantWasteContainersQuery["tenant"]>["wasteContainers"]
>["items"][number];

type ContainerRow = WasteContainerListItemFragment & {
  facility?: { id: string; name?: string | null } | null;
};

const CONTAINER_STATUS_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  Object.entries(CONTAINER_STATUS_LABELS)
);
const CONTAINER_TYPE_ENUM_VALUES = WASTE_CONTAINER_TYPES.map((type) => type.code);

function containerStatusFilterLabel(value: string): string {
  return CONTAINER_STATUS_LABEL_BY_VALUE[value] ?? value;
}

const CLOCK_STATUS_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  Object.entries(CLOCK_STATUS_LABELS)
);

function clockStatusFilterLabel(value: string): string {
  return CLOCK_STATUS_LABEL_BY_VALUE[value] ?? value;
}

/**
 * Keyed omnisearch filters for the containers grids (Task 8 backend keys).
 * These are independent of the grid columns: `area`/`wasteStream` filter on a
 * different key than their column's `field` (which drives sort, matching the
 * backend's `accumulationArea`/`wasteStream` sortable names), so declaring
 * them as columns would filter on the wrong key. `facilityId` only applies
 * to the tenant grid, which isn't already scoped to one facility; its
 * `wasteFacility` control always emits a `facilityId:<uuid>` term (see
 * stringifyOmnisearchWithAliases), so the declared key must match that.
 */
function buildAdditionalFilterKeys(includeFacility: boolean): FilterKey[] {
  const keys: FilterKey[] = [
    { key: "area", header: "Area" },
    {
      key: "status",
      header: "Status",
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(WasteContainerStatus),
      enumPresentationFunction: containerStatusFilterLabel,
    },
    {
      key: "containerType",
      header: "Container Type",
      filterKeyType: "enumAutocomplete",
      enumValues: CONTAINER_TYPE_ENUM_VALUES,
      enumPresentationFunction: containerTypeLabel,
    },
    { key: "wasteStream", header: "Waste Stream" },
    {
      key: "clock",
      header: "Clock / Limit",
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(ClockStatus),
      enumPresentationFunction: clockStatusFilterLabel,
    },
    {
      key: "classification",
      header: "Classification",
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(WasteClassification),
      enumPresentationFunction: prettyPrintWasteClassification,
    },
  ];
  if (includeFacility) {
    keys.push({ key: "facilityId", header: "Facility", filterKeyType: "wasteFacility" });
  }
  return keys;
}

/**
 * Column fields match the backend's sortable names (deadlineDate is the
 * persisted min-deadline column the clock's effectiveDeadlineDate mirrors).
 */
function buildColumns<TRow extends ContainerRow>(
  includeFacility: boolean,
  tenantId: string,
  linkWasteStream: boolean,
  showClockTooltip: boolean
): OmnisearchGridColDef<TRow>[] {
  const columns: OmnisearchGridColDef<TRow>[] = [
    {
      field: "containerLabel",
      headerName: "Container",
      flex: 0.5,
      minWidth: 110,
      sortable: true,
      filterable: false,
    },
    {
      field: "wasteStream",
      headerName: "Waste Stream",
      // Wide: the cell carries the name, the profile number and a
      // classification chip that runs to "RCRA Acutely Hazardous", and at a
      // narrower width the chip wins the space and the name truncates to a
      // few characters — the opposite of what a reader is scanning for.
      flex: 1.6,
      minWidth: 320,
      sortable: true,
      filterable: false,
      // Sorting and export keep using the plain name; only the cell is rich.
      valueGetter: (_, row) => row.wasteStream?.name ?? NO_VALUE_PLACEHOLDER,
      renderCell: ({ row }) => (
        <WasteStreamCell row={row} tenantId={tenantId} linkStream={linkWasteStream} />
      ),
    },
    {
      field: "accumulationArea",
      headerName: "Area",
      flex: 0.7,
      minWidth: 150,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => accumulationAreaLabel(row.accumulationArea),
    },
    {
      field: "amount",
      headerName: "Fill",
      flex: 0.6,
      minWidth: 130,
      sortable: true,
      filterable: false,
      // The export gets the numbers the cell draws a bar over.
      valueGetter: (_, row) => formatAmountCapacity(row),
      renderCell: ({ row }) => <FillCell row={row} />,
    },
    {
      field: "deadlineDate",
      headerName: "Clock",
      flex: 0.5,
      minWidth: 110,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) => <ClockCell row={row} showClockTooltip={showClockTooltip} />,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 130,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) => <WasteContainerStatusChip status={row.status} />,
    },
    {
      field: "containerType",
      headerName: "Container Type",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      filterable: false,
      headerClassName: "wrapHeader",
      valueGetter: (_, row) => containerTypeLabel(row.containerType),
    },
  ];
  if (includeFacility) {
    columns.splice(2, 0, {
      field: "facility",
      headerName: "Facility",
      flex: 0.7,
      minWidth: 130,
      sortable: true,
      // Filtering is the omnisearch bar's job on this grid, not the column
      // menu's; `facilityId` is declared as a key above.
      filterable: false,
      valueGetter: (_, row) => row.facility?.name ?? NO_VALUE_PLACEHOLDER,
    });
  }
  return columns;
}

const INITIAL_SORT: GridSortModel = [{ field: "deadlineDate", sort: "asc" }];

/**
 * Container type is off by default: the specs' grid doesn't carry it, and the
 * capacity it implies is already in the Fill column. Still available from the
 * column menu for anyone who sorts by drum size.
 */
const INITIAL_GRID_STATE = {
  columns: { columnVisibilityModel: { containerType: false } },
};

const getTenantItems = (data: TenantWasteContainersQuery) =>
  data.tenant?.wasteContainers?.items ?? [];
const getTenantCount = (data: TenantWasteContainersQuery) =>
  data.tenant?.wasteContainers?.count ?? 0;
const getFacilityItems = (data: FacilityWasteContainersQuery) =>
  data.facility?.wasteContainers?.items ?? [];
const getFacilityCount = (data: FacilityWasteContainersQuery) =>
  data.facility?.wasteContainers?.count ?? 0;

const toApiSortModel = (sort: GridSortModel) => sort.map((s) => ({ field: s.field, sort: s.sort }));

type ContainersGridCommandButtons =
  | React.ReactNode[]
  | ((context: OmnisearchCommandButtonsContext) => React.ReactNode[]);

/**
 * The container list rendered for either scope. The tenant and facility grids
 * differ only in their query, data getters, persistence key, empty-row copy,
 * whether the facility column shows, and which id keys the variables — so those
 * are props while every shared grid setting (filters on, initial sort, row
 * selection for the bulk label action) lives here once and can't drift between
 * the two. Saved-view presets come for free from `persistenceKey` +
 * `showFilters` — the popover owns them internally (see Omnisearch.tsx).
 */
function ContainersGrid<
  TData,
  TVariables extends OperationVariables,
  TRow extends ContainerRow & GridValidRowModel,
>({
  tenantId,
  persistenceKey,
  query,
  getItems,
  getCount,
  buildVariables,
  noRowsMessage,
  includeFacility,
  onRowClick,
  commandButtons,
  showClockTooltip = false,
  checkboxSelection = false,
  compact = false,
}: {
  tenantId: string;
  persistenceKey: string;
  query: TypedDocumentNode<TData, TVariables>;
  getItems: (data: TData) => TRow[];
  getCount: (data: TData) => number;
  buildVariables: (
    omnisearch: string,
    pagination: { page: number; pageSize: number },
    sort: GridSortModel
  ) => TVariables;
  noRowsMessage: string;
  includeFacility: boolean;
  onRowClick: (params: GridRowParams<ContainerRow>) => void;
  commandButtons: ContainersGridCommandButtons;
  /**
   * Shows the clock chip's rule-source tooltip. Off by default so the plain
   * tenant/facility lists keep their lean chip; the area-detail embed turns
   * it on since that's the screen this is meant to explain.
   */
  showClockTooltip?: boolean;
  /**
   * Off by default: the checkbox column is only useful to a caller that also
   * wraps this grid in a `GridSelectionProvider` and offers a bulk action
   * for the selection (the standalone containers list page does both). A
   * caller that doesn't -- e.g. the containers grid embedded on an
   * accumulation area's detail page -- would otherwise show a checkbox
   * column with nothing to do with what it selects.
   */
  checkboxSelection?: boolean;
  /**
   * Drop the grid's own outer padding and tighten its toolbar gap, for a caller
   * that embeds this grid in a card already supplying padding.
   */
  compact?: boolean;
}) {
  const { featureFlags } = useFeatureFlags();
  const linkWasteStream = !!featureFlags[FeatureFlagKey.ManifestViewsV2];
  const columns = useMemo(
    () => buildColumns<TRow>(includeFacility, tenantId, linkWasteStream, showClockTooltip),
    [includeFacility, tenantId, linkWasteStream, showClockTooltip]
  );
  const additionalFilterKeys = useMemo(
    () => buildAdditionalFilterKeys(includeFacility),
    [includeFacility]
  );
  return (
    <OmnisearchDataGridV2
      compact={compact}
      persistenceKey={persistenceKey}
      columns={columns}
      showFilters
      additionalFilterKeys={additionalFilterKeys}
      query={query}
      getItems={getItems}
      getCount={getCount}
      buildVariables={buildVariables}
      noRowsMessage={noRowsMessage}
      initialSortModel={INITIAL_SORT}
      initialState={INITIAL_GRID_STATE}
      onRowClick={onRowClick}
      // Opt-in: checkboxes feed the page header's bulk label action, and a
      // grid embedded somewhere without one (the area detail page) would just
      // be showing dead UI. A row click still opens the drawer either way.
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick
      commandButtons={commandButtons}
      tenantId={tenantId}
    />
  );
}

function TenantContainersGrid({
  tenantId,
  onRowClick,
  checkboxSelection,
  compact,
}: {
  tenantId: string;
  onRowClick: (params: GridRowParams<ContainerRow>) => void;
  checkboxSelection?: boolean;
  /** Forwarded to the grid — see `ContainersGrid`. */
  compact?: boolean;
}) {
  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): TenantWasteContainersQueryVariables => ({
      tenantId,
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toApiSortModel(sort),
    }),
    [tenantId]
  );
  const gridCommandButtons = useCallback(
    ({ search }: OmnisearchCommandButtonsContext) => [
      <ExportDataButton
        key="export"
        tenantId={tenantId}
        exportType="wasteContainers"
        fileName="tenant-waste-containers"
        filter={{ search }}
        iconOnly
      />,
    ],
    [tenantId]
  );
  return (
    <ContainersGrid<
      TenantWasteContainersQuery,
      TenantWasteContainersQueryVariables,
      TenantContainerRow
    >
      tenantId={tenantId}
      persistenceKey="tenant-waste-containers-table"
      query={TENANT_WASTE_CONTAINERS_QUERY}
      getItems={getTenantItems}
      getCount={getTenantCount}
      buildVariables={buildVariables}
      noRowsMessage="No containers are being tracked for this organization."
      includeFacility
      onRowClick={onRowClick}
      commandButtons={gridCommandButtons}
      checkboxSelection={checkboxSelection}
      compact={compact}
    />
  );
}

export function FacilityContainersGrid({
  tenantId,
  facilityId,
  onRowClick,
  commandButtons,
  pinnedSearch,
  persistenceKey = "facility-waste-containers-table",
  showClockTooltip,
  checkboxSelection,
  compact,
}: {
  tenantId: string;
  facilityId: string;
  onRowClick: (params: GridRowParams<ContainerRow>) => void;
  commandButtons: React.ReactNode[];
  /**
   * A search term ANDed onto every request regardless of the visible
   * omnisearch box — e.g. `areaId:<id>` when this grid is embedded on an
   * area's detail page. The box itself stays empty so the user's own
   * free-text search still narrows further, but can't remove the pin.
   */
  pinnedSearch?: string;
  /**
   * Defaults to the standalone containers page's key. Callers embedding
   * this grid elsewhere (e.g. an area's detail page) should pass a distinct
   * key so persisted column/sort/filter settings don't bleed between the
   * two grids.
   */
  persistenceKey?: string;
  /** See `ContainersGrid`'s prop of the same name. */
  showClockTooltip?: boolean;
  /**
   * Off by default -- see `ContainersGrid`'s doc comment. The standalone
   * containers list page turns this on; embedded call sites (e.g. an
   * accumulation area's detail page) that have no bulk action for a
   * selection should leave it off.
   */
  checkboxSelection?: boolean;
  /** Forwarded to the grid -- see `ContainersGrid`. */
  compact?: boolean;
}) {
  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): FacilityWasteContainersQueryVariables => ({
      facilityId,
      search: [pinnedSearch, omnisearch].filter(Boolean).join(" "),
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toApiSortModel(sort),
    }),
    [facilityId, pinnedSearch]
  );
  const gridCommandButtons = useCallback(
    ({ search }: OmnisearchCommandButtonsContext) => [
      ...commandButtons,
      <ExportDataButton
        key="export"
        tenantId={tenantId}
        facilityId={facilityId}
        exportType="wasteContainers"
        fileName="facility-waste-containers"
        filter={{ search }}
        iconOnly
      />,
    ],
    [commandButtons, tenantId, facilityId]
  );
  return (
    <ContainersGrid<
      FacilityWasteContainersQuery,
      FacilityWasteContainersQueryVariables,
      WasteContainerListItemFragment
    >
      tenantId={tenantId}
      persistenceKey={persistenceKey}
      query={FACILITY_WASTE_CONTAINERS_QUERY}
      getItems={getFacilityItems}
      getCount={getFacilityCount}
      buildVariables={buildVariables}
      noRowsMessage="No containers are being tracked at this facility."
      includeFacility={false}
      onRowClick={onRowClick}
      commandButtons={gridCommandButtons}
      showClockTooltip={showClockTooltip}
      checkboxSelection={checkboxSelection}
      compact={compact}
    />
  );
}

/**
 * The page's title row: what this list is, and the two things a reader does
 * from it that aren't filtering.
 *
 * Generate labels sits beside Add container rather than in the grid toolbar
 * because it acts on the checked rows, and the specs put the actions that
 * change containers above the search rather than inside it.
 */
function ContainersPageHeader({ onAddContainer }: { onAddContainer: () => void }) {
  return (
    <Stack
      direction="row"
      alignItems="flex-end"
      justifyContent="space-between"
      spacing={2}
      flexWrap="wrap"
      useFlexGap
    >
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "1px" }}>
          On-site waste
        </Typography>
        <Typography variant="h5" component="h1">
          Containers
        </Typography>
      </Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <BulkExportLabelsButton />
        <Button variant="contained" size="small" startIcon={<Add />} onClick={onAddContainer}>
          Add container
        </Button>
      </Stack>
    </Stack>
  );
}

/** The drawer and its label dialog both live in the path, so opening and
 * closing them is navigation rather than state. Split out of the page so its
 * body stays a layout. */
function useContainerDrawerNavigation({
  containersPath,
  search,
  selectedId,
}: {
  containersPath: string;
  search: string;
  selectedId: string | undefined;
}) {
  const navigate = useNavigate();

  const closeDrawer = useCallback(() => {
    navigate(`${containersPath}${search}`, { preventScrollReset: true });
  }, [navigate, containersPath, search]);

  const setLabelOpen = useCallback(
    (nextOpen: boolean) => {
      if (!selectedId) return;
      const path = nextOpen
        ? `${containersPath}/${selectedId}/label`
        : `${containersPath}/${selectedId}`;
      navigate(`${path}${search}`, { preventScrollReset: true });
    },
    [navigate, containersPath, search, selectedId]
  );

  return { closeDrawer, setLabelOpen };
}

export function WasteContainers() {
  const { tenantId } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  // The container id lives in the path, so a QR code or a deep link opens the
  // drawer over this list instead of a page the reader has to navigate back
  // out of.
  const { selectedId, pushIdToUrl } = useSelectedIdInUrl("containerId");
  const location = useLocation();
  const containersPath = `/o/${tenantId}/waste/containers`;
  // The label dialog lives in the path too, so it can be linked to and
  // survives a refresh — the drawer already works this way, and a dialog the
  // reader can't send anyone to would be the odd one out.
  const labelOpen = location.pathname.endsWith("/label");

  const { closeDrawer, setLabelOpen } = useContainerDrawerNavigation({
    containersPath,
    search: location.search,
    selectedId,
  });

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Containers",
    },
  ]);

  const changeFacilityScope = useCallback(
    (nextFacilityId: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (nextFacilityId) {
        next.set("facilityId", nextFacilityId);
      } else {
        next.delete("facilityId");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<ContainerRow>) => {
      pushIdToUrl(params.row.id);
    },
    [pushIdToUrl]
  );

  // The facility-scoped grid keeps its own Add button in the toolbar for
  // callers that embed it (the area detail page); this page's header owns the
  // one the reader sees here, so nothing extra is passed through.
  const facilityGridCommandButtons = useMemo(() => [], []);

  if (!tenantId) {
    return null;
  }

  return (
    <GridSelectionProvider>
      <ContainersFilterBar facilityId={facilityId} onChangeFacility={changeFacilityScope} />
      <WasteListPageSurface>
        <Stack spacing={3}>
          {/* The grid brings its own horizontal padding; everything above it
              has to match by hand or the title and tiles sit outside the
              card. */}
          <Stack spacing={3} sx={{ px: 3 }}>
            <ContainersPageHeader onAddContainer={() => setAddDialogOpen(true)} />
            <ContainerStatTiles tenantId={tenantId} facilityId={facilityId ?? undefined} />
          </Stack>
          <Box>
            {facilityId ? (
              <FacilityContainersGrid
                tenantId={tenantId}
                facilityId={facilityId}
                onRowClick={handleRowClick}
                commandButtons={facilityGridCommandButtons}
                checkboxSelection
              />
            ) : (
              <TenantContainersGrid
                tenantId={tenantId}
                onRowClick={handleRowClick}
                checkboxSelection
              />
            )}
          </Box>
        </Stack>
      </WasteListPageSurface>
      <WasteContainerDrawer
        containerId={selectedId}
        open={!!selectedId}
        onClose={closeDrawer}
        labelOpen={labelOpen}
        onLabelOpenChange={setLabelOpen}
      />
      {addDialogOpen && (
        <WasteContainerDialog
          onClose={() => setAddDialogOpen(false)}
          facilityId={facilityId ?? undefined}
          onCreated={(containerId) => pushIdToUrl(containerId)}
        />
      )}
    </GridSelectionProvider>
  );
}
